import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useUTM } from "context/UTMContext";
import styles from "../../styles.module.scss";

// ------------------------------------------------------------
// Format ISO date → dd/mm/yyyy
// ------------------------------------------------------------
const formatDateDMY = (value) => {
  if (!value) return "dd/mm/yy";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "dd/mm/yy";
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  return `${day}/${month}/${d.getFullYear()}`;
};

// ------------------------------------------------------------
// Status icon
// ------------------------------------------------------------
const StatusIcon = ({ status }) => {
  if (status === "active") {
    return (
      <svg
        viewBox="0 0 16 16"
        width="12"
        height="12"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <circle cx="8" cy="8" r="8" fill="currentColor" />
        <path
          d="M4.5 8.5L7 11L11.5 5.5"
          stroke="#fff"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  if (status === "inactive") {
    return (
      <svg
        viewBox="0 0 16 16"
        width="12"
        height="12"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
      >
        <circle cx="8" cy="8" r="8" fill="currentColor" />
        <path
          d="M5 8H11"
          stroke="#fff"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
      </svg>
    );
  }

  // expired
  return (
    <svg
      viewBox="0 0 16 16"
      width="12"
      height="12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <circle cx="8" cy="8" r="8" fill="currentColor" />
      <path
        d="M5.5 5.5L10.5 10.5M10.5 5.5L5.5 10.5"
        stroke="#fff"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
};

const PromoCodesSeekerCard = ({ promo }) => {
  const navigate = useNavigate();
  const { t } = useTranslation("global");
  const { addUTMsToUrl } = useUTM();

  const places = Array.isArray(promo.places) ? promo.places : [];

  // ------------------------------------------------------------
  // Status resolution — three states:
  //   expired  → expiration_date has passed (regardless of `active`)
  //   inactive → active === false (and not expired)
  //   active   → active === true and not expired
  // ------------------------------------------------------------
const status = (() => {
  // 1. Expired by date takes precedence
  if (promo.expiration_date) {
    const end = new Date(promo.expiration_date);
    if (!isNaN(end.getTime())) {
      end.setHours(0, 0, 0, 0); // start of expiration day — already expired
      if (new Date() > end) return "expired";
    }
  }

  // 2. Backend explicitly deactivated
  if (promo.active === false) return "inactive";

  // 3. Default — active
  return "active";
})();

  const hasStartDate = !!promo.start_date;
  const hasCreatedAt = !!promo.created_at;

  const gridColumns = 4 + (hasStartDate ? 1 : 0) + (hasCreatedAt ? 1 : 0);

  // Label + CSS class per state
  const statusLabel =
    status === "active"
      ? t("dashboard.seekerPromoCodes.active")
      : status === "inactive"
      ? t("dashboard.seekerPromoCodes.inactive")
      : t("dashboard.seekerPromoCodes.expired");

  const statusClass =
    status === "active"
      ? styles.statusActive
      : status === "inactive"
      ? styles.statusInactive
      : styles.statusExpired;

  return (
    <div className={styles.card}>
      {/* ---------- Promo header band ---------- */}
      <div
        className={styles.promoHeaderBand}
        style={{ "--columns": gridColumns }}
      >
        <div className={styles.promoHeaderCell}>
          <p className={styles.promoLabel}>
            {t("dashboard.seekerPromoCodes.code")}
          </p>
          <p className={styles.promoValueBig}>{promo.title || "—"}</p>
        </div>

        <div className={styles.promoHeaderCell}>
          <p className={styles.promoLabel}>
            {t("dashboard.seekerPromoCodes.discount")}
          </p>
          <p className={styles.promoValueBig}>{promo.percentage ?? "—"}%</p>
        </div>

        {hasStartDate && (
          <div className={styles.promoHeaderCell}>
            <p className={styles.promoLabel}>
              {t("dashboard.seekerPromoCodes.startDate")}
            </p>
            <p className={styles.promoValue}>
              {formatDateDMY(promo.start_date)}
            </p>
          </div>
        )}

        {hasCreatedAt && (
          <div className={styles.promoHeaderCell}>
            <p className={styles.promoLabel}>
              {t("dashboard.seekerPromoCodes.createdAt")}
            </p>
            <p className={styles.promoValue}>
              {formatDateDMY(promo.created_at)}
            </p>
          </div>
        )}

        <div className={styles.promoHeaderCell}>
          <p className={styles.promoLabel}>
            {t("dashboard.seekerPromoCodes.expirationDate")}
          </p>
          <p className={styles.promoValue}>
            {formatDateDMY(promo.expiration_date)}
          </p>
        </div>

        <div className={styles.promoHeaderCell}>
          <p className={styles.promoLabel}>
            {t("dashboard.seekerPromoCodes.status")}
          </p>
          <span className={`${styles.statusBadge} ${statusClass}`}>
            <StatusIcon status={status} />
            {statusLabel}
          </span>
        </div>
      </div>

      {/* ---------- Applied places ---------- */}
      {places.length > 0 && (
        <div className={styles.placesList}>
          {places.map((place) => {
            const image = place.thumbnail_url || null;

            return (
              <div key={place.id} className={styles.placeRow}>
                <div className={styles.placeImageWrapper}>
                  {image ? (
                    <img
                      src={image}
                      alt={place.name}
                      className={styles.placeImage}
                      loading="lazy"
                    />
                  ) : (
                    <div className={styles.placeImagePlaceholder}>
                      {place.name?.charAt(0)?.toUpperCase() || "?"}
                    </div>
                  )}
                </div>

                <div className={styles.placeContent}>
                  <div className={styles.placeHeader}>
                    <h4 className={styles.placeName}>{place.name}</h4>

                    {place.simplified_id && (
                      <p className={styles.placeReference}>
                        <span className={styles.placeReferenceLabel}>
                          {t("dashboard.seekerPromoCodes.spaceRef")}
                        </span>{" "}
                        : {place.simplified_id}
                      </p>
                    )}

                    {place.place_usage?.name && (
                      <p className={styles.placeUsage}>
                        {place.place_usage.name}
                      </p>
                    )}
                  </div>

                  <button
                    type="button"
                    className={styles.reserveButton}
                    onClick={() =>
                      navigate(addUTMsToUrl(`/space/${place.simplified_id}`))
                    }
                  >
                    {t("dashboard.seekerPromoCodes.reserve")}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PromoCodesSeekerCard;